import type { TRISourceReductionQuantityDataType } from '../../types/form-r.ts';
import type { QuestionnaireAnswers } from '../../types/questionnaires.ts';
import type { CasOrCategory } from '../../types/db.ts';
import type { Warning } from '../../types/tri.ts';

const naResult: Pick<TRISourceReductionQuantityDataType, 'ProductionRatio' | 'ProductionRatioType'> = {
  ProductionRatio: {
    choice: 'NA',
    value: true,
  },
  ProductionRatioType: null,
};

export function formRSection89ProductionRatio({
  chemicalName,
  casOrCategory,
  productionOrActivityRatio,
  hasPriorYearData,
  warnings,
}: {
  chemicalName: string;
  casOrCategory: CasOrCategory;
  productionOrActivityRatio: QuestionnaireAnswers['productionOrActivityRatio'];
  hasPriorYearData: boolean;
  warnings: Warning[];
}): Pick<TRISourceReductionQuantityDataType, 'ProductionRatio' | 'ProductionRatioType'> {
  const perChemical = productionOrActivityRatio.byChemical?.[casOrCategory];
  const ratioValues = productionOrActivityRatio.isChemicalSpecific
    ? (perChemical ?? { type: null, priorYearValue: '', currentYearValue: '' })
    : productionOrActivityRatio;

  if (ratioValues.type === null) {
    warnings.push({
      warningID: 'TW1025',
      type: 'questionnaire',
      subType: 'productionOrActivityRatio',
      casOrCategory,
      message: `${chemicalName} (${casOrCategory}), a production or activity ratio was not provided.`,
      blocksPriorYearSection8Calculation: false,
    });
    return naResult;
  }

  if (ratioValues.type === 'N/A') {
    if (hasPriorYearData) {
      warnings.push({
        warningID: 'TW1026',
        type: 'questionnaire',
        subType: 'productionOrActivityRatio',
        casOrCategory,
        message: `${chemicalName} (${casOrCategory}), we saw the production/ratio was marked as N/A. Generally, this answer is only applicable if the releases are due to a new production process or line of business. We have data from the prior year, so this is unlikely. On the questionnaire, please provide production or activity values for the current reporting year and the prior year.`,
        blocksPriorYearSection8Calculation: false,
      });
    }

    return naResult;
  }

  if (typeof ratioValues.priorYearValue !== 'number' || typeof ratioValues.currentYearValue !== 'number') {
    warnings.push({
      warningID: 'TW1027',
      type: 'questionnaire',
      subType: 'productionOrActivityRatio',
      casOrCategory,
      message: `${chemicalName} (${casOrCategory}), numbers required to compute a production or activity ratio were not provided.`,
      blocksPriorYearSection8Calculation: false,
    });
    return naResult;
  }

  if (ratioValues.priorYearValue === 0) {
    warnings.push({
      warningID: 'TW1028',
      type: 'questionnaire',
      subType: 'productionOrActivityRatio',
      casOrCategory,
      message: `${chemicalName} (${casOrCategory}), the prior-year production or activity value was "0". Please provide a non-zero number, or mark this section as N/A on the questionnaire due to a new production process or line of business.`,
      blocksPriorYearSection8Calculation: false,
    });
    return naResult;
  }

  const ratio = ratioValues.currentYearValue / ratioValues.priorYearValue;
  return {
    ProductionRatio: {
      choice: 'ProductionRatioMeasure',
      value: ratio,
    },
    ProductionRatioType: ratioValues.type === 'production' ? 'PRODUCTION' : 'ACTIVITY',
  };
}
